import { useEffect, useRef } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { HelpIcon } from "@/components/ui/help-icon";
import { cn } from "@/lib/utils";
import { useLogStream } from "@/hooks/use-log-stream";
import { useLogSettings } from "@/hooks/use-log-settings";
import { Pause, Play, Trash2, Wifi, WifiOff, ScrollText } from "lucide-react";

export default function LogsPage() {
  const { lines, isConnected, isPaused, togglePause, clearLogs } = useLogStream();
  const { debugLevel, verboseLevel, setDebugLevel, setVerboseLevel, isLoading } =
    useLogSettings();

  const terminalRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  useEffect(() => {
    if (autoScrollRef.current && terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [lines]);

  const handleScroll = () => {
    if (!terminalRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = terminalRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  };

  return (
    <div className="flex h-full min-h-0 flex-1">
      <aside className="flex w-64 shrink-0 flex-col gap-4 border-r border-border bg-sidebar p-4 overflow-y-auto scrollbar-thin">
        <div className="flex items-center gap-2">
          <ScrollText size={16} className="text-muted-foreground" />
          <span className="text-sm font-semibold">Log Settings</span>
          <HelpIcon text="Controls Asterisk logging verbosity via AMI. Higher levels produce more detail in the stream; they apply immediately and affect server log volume." />
        </div>

        <Separator />

        <div className="space-y-3" role="group" aria-label="Logging controls">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">
                Debug Level
              </label>
              <span className="text-xs font-mono text-foreground tabular-nums">
                {debugLevel}
              </span>
            </div>
            <Slider
              value={debugLevel}
              min={0}
              max={10}
              onChange={setDebugLevel}
              disabled={isLoading}
            />
            <p className="text-[11px] text-muted-foreground/70 font-mono">
              core set debug {debugLevel}
            </p>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">
                Verbose Level
              </label>
              <span className="text-xs font-mono text-foreground tabular-nums">
                {verboseLevel}
              </span>
            </div>
            <Slider
              value={verboseLevel}
              min={0}
              max={10}
              onChange={setVerboseLevel}
              disabled={isLoading}
            />
            <p className="text-[11px] text-muted-foreground/70 font-mono">
              core set verbose {verboseLevel}
            </p>
          </div>
        </div>

        <Separator />

        <div className="space-y-2">
          <div className="flex items-center text-xs font-medium text-muted-foreground">
            SIP Traces
            <HelpIcon text="SIP packet traces are captured to Homer over HEP, not to this log stream. Asterisk's packet logger is intentionally not exposed here." />
          </div>
          <p className="text-[11px] text-muted-foreground/70">
            Inspect SIP signalling in Homer.
          </p>
        </div>

        <Separator />

        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            {isConnected ? (
              <Wifi size={14} className="text-success" />
            ) : (
              <WifiOff size={14} className="text-destructive" />
            )}
            <span className="text-xs text-muted-foreground">
              {isConnected ? "Connected" : "Reconnecting..."}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs"
              onClick={togglePause}
            >
              {isPaused ? <Play size={13} /> : <Pause size={13} />}
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={clearLogs}
              aria-label="Clear logs"
              title="Clear logs"
            >
              <Trash2 size={13} />
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-background px-4">
          <span className="text-xs font-medium text-foreground">
            Server Logs
          </span>
          <HelpIcon text="Live stream of the Asterisk manager log over WebSocket. Auto-scrolls while you are at the bottom; scroll up to inspect history without being pulled down." />
          <div className="flex-1" />
          {isPaused && <Badge variant="warning">Paused</Badge>}
          <span className="text-xs text-muted-foreground tabular-nums">
            {lines.length} lines
          </span>
        </div>
        <div
          ref={terminalRef}
          onScroll={handleScroll}
          className="min-h-0 flex-1 overflow-auto bg-card p-4 font-mono text-xs leading-5 scrollbar-thin"
        >
          {lines.length === 0 && (
            <div className="flex h-full select-none items-center justify-center text-muted-foreground">
              {isConnected ? "Waiting for logs..." : "Connecting..."}
            </div>
          )}
          {lines.map((line, i) => (
            <div
              key={i}
              className={cn(
                "whitespace-pre-wrap break-all",
                line.includes("WARNING") && "text-warning-text",
                line.includes("ERROR") && "text-destructive-text",
                line.includes("NOTICE") && "text-success-text",
                line.includes("DEBUG") && "text-info-text",
                line.includes("VERBOSE") && "text-info-text",
                !line.includes("WARNING") &&
                  !line.includes("ERROR") &&
                  !line.includes("NOTICE") &&
                  !line.includes("DEBUG") &&
                  !line.includes("VERBOSE") &&
                  "text-foreground"
              )}
            >
              {line || " "}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
